import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { AI_AGENT_UI } from './tokens';

export type AgentInlineSelectOption = {
  value: string | number;
  label: React.ReactNode;
  disabled?: boolean;
  /** Extra left padding (e.g. nested collections). */
  indent?: number;
};

type MenuCoords = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  openUp: boolean;
};

type Props = {
  id?: string;
  label: string;
  value: string | number;
  options: AgentInlineSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Prefer opening upward when true (still flips if not enough space). */
  openAbove?: boolean;
  maxListHeight?: number;
  sx?: Record<string, unknown>;
  emptyLabel?: string;
};

/**
 * Viewport-anchored overlay select (works in zoomed layouts and the help assistant strip).
 */
export function AgentInlineSelect({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
  fullWidth,
  openAbove = false,
  maxListHeight = 240,
  sx,
  emptyLabel = 'No options',
}: Props) {
  const reactId = useId();
  const fieldId = id ?? `agent-inline-select-${reactId}`;
  const listId = `${fieldId}-listbox`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const displayLabel = selected?.label ?? null;

  const updateCoords = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const preferUp = openAbove || spaceBelow < Math.min(140, maxListHeight);
    const openUp = preferUp && spaceAbove > spaceBelow;
    const available = openUp ? spaceAbove : spaceBelow;
    // Empty lists still need enough room for the empty-state message (avoid a thin blank strip).
    const minMenu = options.length === 0 ? 52 : 120;
    const height = Math.max(minMenu, Math.min(maxListHeight, available));

    setCoords({
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
      width: Math.max(160, rect.width),
      maxHeight: height,
      openUp,
    });
  }, [maxListHeight, openAbove, options.length]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updateCoords();
  }, [open, updateCoords, options.length]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => updateCoords();

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReposition);
    // Capture scroll from nested chat panes too.
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateCoords]);

  const toggle = () => {
    if (disabled) return;
    setOpen((v) => !v);
  };

  const pick = (opt: AgentInlineSelectOption) => {
    if (opt.disabled) return;
    onChange(String(opt.value));
    setOpen(false);
  };

  const menu =
    open &&
    coords &&
    createPortal(
      <Box
        ref={menuRef}
        id={listId}
        role="listbox"
        aria-labelledby={fieldId}
        sx={{
          position: 'fixed',
          ...(coords.openUp ? { bottom: coords.bottom } : { top: coords.top }),
          left: coords.left,
          width: coords.width,
          maxHeight: coords.maxHeight,
          overflowY: 'auto',
          overflowX: 'auto',
          zIndex: 1400,
          borderRadius: `${AI_AGENT_UI.radius.md}px`,
          border: `1px solid ${AI_AGENT_UI.border}`,
          bgcolor: AI_AGENT_UI.surface,
          boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(15, 23, 42, 0.08)',
          py: 0.5,
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(100, 116, 139, 0.55)',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(148, 163, 184, 0.12)',
          },
        }}
      >
        {options.length === 0 ? (
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              fontSize: AI_AGENT_UI.fontSm,
              fontWeight: 500,
              color: AI_AGENT_UI.textSecondary,
              whiteSpace: 'normal',
              lineHeight: 1.35,
            }}
          >
            {emptyLabel}
          </Box>
        ) : (
          options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <Box
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                onClick={() => pick(opt)}
                sx={{
                  px: 1.5,
                  py: 0.85,
                  pl: 1.5 + (opt.indent ?? 0) * 1.25,
                  fontSize: AI_AGENT_UI.font,
                  lineHeight: 1.3,
                  color: opt.disabled ? AI_AGENT_UI.textSecondary : AI_AGENT_UI.text,
                  bgcolor: isSelected ? 'rgba(29, 78, 216, 0.08)' : 'transparent',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: opt.disabled ? 'default' : 'pointer',
                  opacity: opt.disabled ? 0.55 : 1,
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  '&:hover': opt.disabled
                    ? undefined
                    : {
                        bgcolor: isSelected
                          ? 'rgba(29, 78, 216, 0.12)'
                          : AI_AGENT_UI.surfaceMuted,
                      },
                }}
              >
                {opt.label}
              </Box>
            );
          })
        )}
      </Box>,
      document.body,
    );

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: fullWidth ? '100%' : undefined,
        minWidth: 0,
        ...sx,
      }}
    >
      <Typography
        component="label"
        htmlFor={fieldId}
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.01em',
          lineHeight: 1.2,
          color: open ? AI_AGENT_UI.primary : AI_AGENT_UI.textSecondary,
          px: 0.25,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>

      <Box
        component="button"
        ref={triggerRef}
        type="button"
        id={fieldId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        onClick={toggle}
        sx={{
          all: 'unset',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          minHeight: 40,
          maxWidth: '100%',
          px: 1.5,
          py: 0.75,
          borderRadius: `${AI_AGENT_UI.radius.sm}px`,
          border: `1.5px solid ${open ? AI_AGENT_UI.primary : AI_AGENT_UI.borderStrong}`,
          bgcolor: disabled ? AI_AGENT_UI.surfaceMuted : AI_AGENT_UI.surface,
          color: AI_AGENT_UI.text,
          fontSize: AI_AGENT_UI.font,
          fontFamily: 'inherit',
          fontWeight: 400,
          lineHeight: 1.35,
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          overflow: 'hidden',
          appearance: 'none',
          WebkitAppearance: 'none',
          boxShadow: open ? '0 0 0 3px rgba(29, 78, 216, 0.12)' : 'none',
          transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
          '&:hover': disabled
            ? undefined
            : {
                borderColor: open ? AI_AGENT_UI.primary : '#9ca3af',
              },
          '&:focus-visible': {
            borderColor: AI_AGENT_UI.primary,
            boxShadow: '0 0 0 3px rgba(29, 78, 216, 0.16)',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: displayLabel ? AI_AGENT_UI.text : AI_AGENT_UI.textSecondary,
          }}
        >
          {displayLabel ?? 'Select…'}
        </Box>
        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: 20,
            color: AI_AGENT_UI.textSecondary,
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease',
          }}
        />
      </Box>

      {menu}
    </Box>
  );
}
